import {
    Button,
    Input,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    FormControl,
    FormLabel,
    IconButton,
    Box,
    Flex,
} from "@chakra-ui/react";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, ChangeEvent, useEffect } from "react";
import { Dropdown } from "../components/dropdown/dropdown.comp";
import { insuranceTypes } from "../constants/insurace-type.constants";
import { coverageTypes } from "../constants/coverage-type.constants";
import { CoverageType, InsuranceType, PickPolicyCreateRequestExcludeKeyofPolicyCreateRequestCarrierId, Policy, PolicyUpdateRequest } from "@bluefalcon/clients-management-client-fetch";
import { useConfirmDelete } from "../hooks/useConfirmDelete";

type PolicyCreateRequest = PickPolicyCreateRequestExcludeKeyofPolicyCreateRequestCarrierId;

type AddEditPolicyModalProps = {
    isOpen: boolean;
    policy?: Policy;
    handleAddPolicy: (data: PolicyCreateRequest) => Promise<void>;
    handleEditPolicy: (id: number, data: Omit<PolicyUpdateRequest, 'id'>) => Promise<void>;
    handleDeletePolicy: (policyId: number) => Promise<void>;
    onClose: () => void;
};

export const AddEditPolicyModal = ({
    isOpen,
    policy,
    handleAddPolicy,
    handleEditPolicy,
    handleDeletePolicy,
    onClose,
}: AddEditPolicyModalProps) => {

    const [formData, setFormData] = useState<{
        coverageType?: CoverageType,
        insuranceType?: InsuranceType,
        name?: string,
        carrierCodes?: string[],
    }>({
        coverageType: policy?.coverageType,
        insuranceType: policy?.insuranceType,
        name: policy?.name,
        carrierCodes: policy?.carrierCodes ?? [],
    });

    const { ConfirmDeleteModal, confirmDelete } = useConfirmDelete();

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    const handleInsuranceTypeDropdownSelect = (insuranceType?: string) => {
        const found = Object.keys(insuranceTypes).find(
            (key) => insuranceTypes[key as keyof typeof insuranceTypes] === insuranceType
        );
        setFormData((prevFormData: any) => ({
            ...prevFormData,
            insuranceType: found,
        }));
    };

    const handleCoverageTypeDropdownSelect = (coverageType?: string) => {
        const found = Object.keys(coverageTypes).find(
            (key) => coverageTypes[key as keyof typeof coverageTypes] === coverageType
        );
        setFormData((prevFormData: any) => ({
            ...prevFormData,
            coverageType: found,
        }));
    };

    const handleSubmit = async () => {
        if (policy) {
            await handleEditPolicy(policy.id, formData);
        } else if (formData.name?.trim() && formData.coverageType && formData.insuranceType) {
            await handleAddPolicy({
                name: formData.name,
                coverageType: formData.coverageType,
                insuranceType: formData.insuranceType,
                carrierCodes: formData.carrierCodes?.map((c) => c.trim()) ?? [],
            });
        }
    };

    const deletePolicy = async () => {
        if (policy) {
            await handleDeletePolicy(policy.id);
        }
    };

    const handleDelete = () => {
        confirmDelete(deletePolicy, 'policy');
    };

    const close = () => {
        setFormData({});
        onClose();
    };

    useEffect(() => {
        setFormData({
            coverageType: policy?.coverageType,
            insuranceType: policy?.insuranceType,
            name: policy?.name,
            carrierCodes: policy?.carrierCodes?.map((c) => c.trim()) ?? [],
        });
    }, [policy]);

    return (
        <Box key={policy?.id}>
            <Modal isOpen={isOpen} onClose={close}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{policy ? 'Edit Policy' : 'Add Policy'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl isRequired p={2}>
                            <FormLabel htmlFor="name">Name</FormLabel>
                            <Input
                                id="name"
                                name="name"
                                value={formData?.name ?? ""}
                                onChange={handleChange}
                                placeholder="Enter policy name"
                            />
                        </FormControl>
                        <Flex>
                            <Box p={2}>
                                <Dropdown
                                    required={true}
                                    placeholder="Insurance Type"
                                    items={Object.values(insuranceTypes)}
                                    onChange={handleInsuranceTypeDropdownSelect}
                                    value={formData?.insuranceType ? insuranceTypes[formData.insuranceType] : undefined}
                                />
                            </Box>
                            <Box p={2}>
                                <Dropdown
                                    required={true}
                                    placeholder="Coverage Type"
                                    items={Object.values(coverageTypes)}
                                    onChange={handleCoverageTypeDropdownSelect}
                                    value={formData?.coverageType ? coverageTypes[formData.coverageType] : undefined}
                                />
                            </Box>
                        </Flex>
                        <Box>
                            <FormControl>
                                <FormLabel>Carrier Codes</FormLabel>
                                <Input
                                    type="text"
                                    placeholder="Carrier Codes"
                                    value={formData.carrierCodes?.join()}
                                    onChange={(e) => setFormData((prevData) => ({ ...prevData, carrierCodes: e.target.value.split(',') }))}
                                />
                            </FormControl>
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        {policy && (
                            <IconButton
                                aria-label="Delete Policy"
                                icon={<FontAwesomeIcon icon={faTrashCan} />}
                                variant="outline"
                                onClick={handleDelete}
                                mr="auto"
                            />
                        )}
                        <Button variant="outline" onClick={close}>
                            Close
                        </Button>
                        <Button
                            colorScheme="teal"
                            ml={3}
                            onClick={handleSubmit}
                            isDisabled={!formData?.name?.trim() || !formData.coverageType || !formData.insuranceType}
                        >
                            {policy ? "Edit Policy" : "Add Policy"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <ConfirmDeleteModal />
        </Box>
    );
};
